import { Button } from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { ContentHeaderTab } from "@saleor/createOrderDomestic/views/CreateOrderView/const";
import {
  ContractOrderDomesticQuery,
  ContractQuery,
  Get_Realtime_PartnerMutation
} from "@saleor/graphql";
import { makeStyles } from "@saleor/macaw-ui";
import { renderCollection } from "@saleor/misc";
import classNames from "classnames";
import { FormikProps } from "formik";
import React from "react";

import AdditionalTab from "./Tab/AdditionalTab";
import PartnerTab from "./Tab/PartnerTab";
import SalesTab from "./Tab/SalesTab";

interface HeaderTabProps {
  realtimePartnerData: Get_Realtime_PartnerMutation;
  getRealtimePartnerData: () => void;
  activeTab: ContentHeaderTab;
  setActiveTab: (value) => void;
  addressShipTo: any[];
  addressBillTo: any[];
  formik: FormikProps<any>;
  data: ContractOrderDomesticQuery["contractOrder"];
  dataContract: ContractQuery;
  contract: ContractOrderDomesticQuery;
  setBillToInsteaded: (value: string) => void;
  setShipToInsteaded: (value: string) => void;
  salesOrgCode?: string;
  onApplyAll: () => void;
}

const useStyles = makeStyles(
  theme => ({
    root: {
      marginTop: "4rem"
    },
    tableTabActive: {
      WebkitClipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      width: "200px",
      height: "40px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "6px",
      background: theme.palette.info.dark,
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      position: "relative",
      border: 0
    },
    innerTabActive: {
      WebkitClipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      width: "199px",
      height: "39px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "5px",
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      top: 1,
      left: 1,
      background: theme.palette.info.main,
      position: "absolute",
      borderBottom: 0,
      color: "#FFFFFF"
    },
    tableTab: {
      WebkitClipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 78% 0, 100% 100%, 0% 100%)",
      width: "200px",
      height: "40px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "6px",
      background: theme.palette.grey[400],
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      position: "relative",
      border: 0
    },
    innerTab: {
      WebkitClipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      clipPath: "polygon(0 0, 77.5% 0, 99% 100%, 0% 100%)",
      width: "199px",
      height: "39px",
      boxShadow: "2px 0px 4px rgba(0, 0, 0, 0.25)",
      borderTopLeftRadius: "5px",
      display: "flex",
      alignItems: "center",
      paddingLeft: "2.4rem",
      cursor: "pointer",
      top: 1,
      left: 1,
      background: theme.palette.grey[300],
      color: theme.palette.grey[600],
      position: "absolute",
      border: 0
    },
    tabB: {
      marginLeft: "-3rem"
    },
    z1: {
      zIndex: 0
    },
    z2: {
      zIndex: 1
    },
    z3: {
      zIndex: 3
    },
    zMax: {
      zIndex: 100
    },
    tabBox: {
      display: "flex"
    },
    boxContentTab: {
      background: "#FFF",
      border: `solid 1px ${theme.palette.info.dark}`,
      padding: "3.7rem 2.4rem"
    }
  }),
  { name: "HeaderTabCS" }
);

const tabList = [
  {
    id: "partner-tab",
    code: ContentHeaderTab.partner,
    title: "Partner",
    index: "z3",
    first: true
  },
  {
    id: "additional-tab",
    code: ContentHeaderTab.additionalData,
    title: "Additional Data",
    index: "z2",
    first: false
  },
  {
    id: "sales-tab",
    code: ContentHeaderTab.sales,
    title: "Sales",
    index: "z1",
    first: false
  }
];

const HeaderTab: React.FC<HeaderTabProps> = props => {
  const {
    formik,
    data,
    activeTab,
    setActiveTab,
    contract,
    dataContract,
    setBillToInsteaded,
    setShipToInsteaded,
    salesOrgCode,
    getRealtimePartnerData,
    realtimePartnerData,
    onApplyAll
  } = props;

  const classes = useStyles();

  const onTabClick = tab => {
    setActiveTab(tab);
  };

  const renderPartnerTab = (
    <PartnerTab
      getRealtimePartnerData={getRealtimePartnerData}
      realtimePartnerData={realtimePartnerData}
      data={data}
      dataContract={dataContract}
      contract={contract?.contractOrder}
      formik={formik}
      setBillToInsteaded={setBillToInsteaded}
      setShipToInsteaded={setShipToInsteaded}
      onApplyAll={onApplyAll}
    />
  );
  const renderAdditionalTab = <AdditionalTab formik={formik} />;
  const renderSalesTab = (
    <SalesTab
      formik={formik}
      salesOrgCode={salesOrgCode}
      orderTypeDesc={contract?.contractOrder?.orderTypeDesc}
    />
  );

  return (
    <div className={classes.root}>
      <div className={classes.tabBox}>
        {renderCollection(tabList, tab => (
          <div
            key={tab.code}
            className={classNames(
              activeTab === tab.code ? classes.zMax : classes[tab.index]
            )}
          >
            <Button
              id={tab.id}
              data-testid={tab.id}
              onClick={() => onTabClick(tab.code)}
              className={classNames(
                !tab.first && classes.tabB,
                activeTab === tab.code
                  ? classes.tableTabActive
                  : classes.tableTab
              )}
            >
              <MyTypography
                variant={"body2"}
                className={classNames(
                  activeTab === tab.code
                    ? classes.innerTabActive
                    : classes.innerTab
                )}
              >
                {tab.title}
              </MyTypography>
            </Button>
          </div>
        ))}
      </div>
      <div className={classes.boxContentTab}>
        <div hidden={activeTab !== ContentHeaderTab.partner}>
          {renderPartnerTab}
        </div>
        <div hidden={activeTab !== ContentHeaderTab.additionalData}>
          {renderAdditionalTab}
        </div>
        <div hidden={activeTab !== ContentHeaderTab.sales}>
          {renderSalesTab}
        </div>
      </div>
    </div>
  );
};

export default HeaderTab;
